import {ArrowLeftIcon, XIcon} from '@primer/octicons-react'
import {Button, IconButton} from '@primer/react'
import {Dialog, FeatureFlags} from '@primer/react/experimental'
import {memo, Suspense, useEffect, useCallback, useMemo, useRef, useState} from 'react'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {useLocation} from '@github-ui/react-router'
import {
  type CommentsFilterState,
  getDefaultFilterState,
  parseFilterKeywords,
  useFilteredThreadIds,
  useFilterIssueCommentsIds,
} from './comments-panel/CommentsFilter'
import type {PageLimits} from '../../page-data/payloads/files'
import {
  useThreads,
  useIssueComments,
  useReviewSummaries,
  useDiffPathOrderMap,
  usePullRequestBasePath,
} from '../../stores/selectors'
import {
  useCommentReplyActions,
  useCommentsPanelViewMode,
  useLayoutCanComment,
  useLayoutStoreSeeded,
  useSelectedCommentReplyThreadId,
  useLayoutAuthorLogin,
} from '../../stores/layout-selectors'
import {reviewSummaryToIssueComment, buildConversationItems} from './review-summary-to-issue-comment'
import {useCreatePullRequestTimelineCommentMutation} from '../../mutations/use-create-pull-request-timeline-comment-mutation'
import {ssrSafeDocument} from '@github-ui/ssr-utils'
import {createPortal} from 'react-dom'
import {
  DockedPanel,
  useDockedPanelActions,
  useDockedPanelFocusRefs,
  useDockedPanelMetadata,
  useIsDockedPanelCompact,
  useIsDockedPanelOpen,
} from '../../contexts/DockedPanelContext'
import {useIssueCommentLiveUpdates} from '../../hooks/use-issue-comment-live-updates'
import {useHeaderPageData} from '../../page-data/loaders/use-header-page-data'
import {DOCKED_SIDE_PANEL_CONTENT_ID} from './docked-side-panel-constants'
import {Panel} from './Panel'
import {getPanelHeading} from './panel-registry'
import {noop} from '@github-ui/noop'
import {CommentPanelHeaderContent, isReviewCommentsViewMode} from './comments-panel/CommentPanelHeaderContent'
import {CommentsPanelBody} from './comments-panel/CommentsPanelBody'
import {CommentsSidePanelFooter, type CommentsSidePanelFooterHandle} from './comments-panel/CommentsSidePanelFooter'
import {
  sortThreadPreviewsByDiffOrder,
  buildThreadPreviewsAuthorList,
  buildIssueCommentsAuthorList,
} from '../../helpers/comments-panel-helper'
import {useFileTreeDiffSummaries} from '../../page-data/loaders/use-file-tree-diff-summaries'
import {useInfiniteThreadsPageData} from '../../page-data/loaders/use-threads'
import {useActiveRoute} from '../../hooks/use-active-route'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {produce} from 'immer'

export interface CommentsSidePanelProps {
  pageLimits?: PageLimits
}

/**
 * Renders nothing but lazily fetches file tree and thread data into the store
 * when rendered. Fetches the file tree (non-suspending) and all thread pages
 * (suspending). Must be wrapped in a Suspense boundary for the thread fetch.
 */
function LazyDataLoader({onLoaded}: {onLoaded: () => void}) {
  const basePath = usePullRequestBasePath()

  const {isFetched: isFileTreeFetched} = useFileTreeDiffSummaries({basePath, enabled: true})

  const {
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    isFetched: areThreadsFetched,
  } = useInfiniteThreadsPageData({
    pathName: basePath,
    hasMorePages: true,
  })

  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage) {
      void fetchNextPage()
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage])

  useEffect(() => {
    if (isFileTreeFetched && areThreadsFetched) {
      onLoaded()
    }
  }, [isFileTreeFetched, areThreadsFetched, onLoaded])

  return null
}

/**
 *
 * Renders a sidesheet displaying previews of the pull request's threads or issue comments.
 *
 * Self-contained: sources its own data from hooks/context.
 *
 * Rendering target depends on the `updated_pull_request_header` flag:
 * - **ON**: rendered by `DockedSidePanelContent` and emits `<Panel>` inline; that
 *   parent already lives inside the docked sidebar.
 * - **OFF**: rendered as a child of `OpenCommentsPanelButton` (to inherit
 *   `PullRequestStoreProvider` scope needed by `ThreadPositioningWithStore`),
 *   and portals `<Panel>` into `DOCKED_SIDE_PANEL_CONTENT_ID` so it visually
 *   appears in the sidebar.
 *
 * In compact mode, falls back to a Dialog sidesheet regardless of flag.
 */
// eslint-disable-next-line @typescript-eslint/no-shadow -- named function expression intentionally shares the outer name
export const CommentsSidePanel = memo(function CommentsSidePanel({pageLimits}: CommentsSidePanelProps) {
  const isOpen = useIsDockedPanelOpen(DockedPanel.Comments)
  const isCompact = useIsDockedPanelCompact()
  const {closePanel} = useDockedPanelActions()
  const {returnFocusRef} = useDockedPanelFocusRefs()

  const pathName = usePullRequestBasePath()
  const pullRequestAuthorLogin = useLayoutAuthorLogin()
  const {
    data: {aliveChannels},
  } = useHeaderPageData()

  const viewMode = useCommentsPanelViewMode()
  const selectedThreadId = useSelectedCommentReplyThreadId()
  const {clearReplyDetail, setCommentsPanelViewMode} = useCommentReplyActions()
  const unsortedThreadPreviews = useThreads()
  const totalDiffCommentCount = unsortedThreadPreviews.length
  const diffPathOrderMap = useDiffPathOrderMap()
  const activeRoute = useActiveRoute()
  const isFilesChanged = activeRoute === 'files_changed'

  const threadPreviews = useMemo(
    () => sortThreadPreviewsByDiffOrder(unsortedThreadPreviews, diffPathOrderMap),
    [unsortedThreadPreviews, diffPathOrderMap],
  )
  const issueComments = useIssueComments()
  const reviewSummaries = useReviewSummaries()
  const currentUserCanComment = useLayoutCanComment()

  // Build a chronologically sorted list of conversation items (issue comments + review summaries)
  // preserving original types so each renders with its dedicated component
  const conversationItems = useMemo(
    () => buildConversationItems(issueComments, reviewSummaries),
    [issueComments, reviewSummaries],
  )

  // Flatten to IssueComment shape for filtering (useFilterIssueCommentsIds expects IssueComment[])
  const mergedConversationComments = useMemo(() => {
    const convertedSummaries = reviewSummaries.map(reviewSummaryToIssueComment)
    return [...issueComments, ...convertedSummaries]
  }, [issueComments, reviewSummaries])

  const [threadPreviewFilterState, setThreadPreviewFilterState] = useState<CommentsFilterState>(() =>
    getDefaultFilterState(),
  )
  const [issueCommentsFilterState, setIssueCommentsFilterState] = useState<CommentsFilterState>(() =>
    getDefaultFilterState(),
  )

  // Open panel and apply filter from URL review_id param
  const {openPanel, closePanel: closePanelAction} = useDockedPanelActions()
  const location = useLocation()
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search)
    const reviewId = urlParams.get('review_id')

    if (!reviewId) return
    const showResolved = urlParams.get('show_resolved')
    const resolvedFilter = showResolved === 'true' ? ' is:resolved' : showResolved === 'false' ? ' is:unresolved' : ''
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setThreadPreviewFilterState(prev => ({
      ...prev,
      filterText: `review-id:${reviewId}${resolvedFilter}`.trim(),
    }))
    openPanel(DockedPanel.Comments)

    return () => {
      closePanelAction()
    }
  }, [location.pathname, location.search, openPanel, closePanelAction])

  // Apply panel metadata (e.g. author filter from action items) during render to avoid a
  // double-render from an effect. Tracks the last applied metadata object so the update
  // only fires when metadata changes while the panel is open.
  const panelMetadata = useDockedPanelMetadata()
  const [lastAppliedMetadata, setLastAppliedMetadata] = useState(panelMetadata)
  if (isOpen && panelMetadata && panelMetadata !== lastAppliedMetadata) {
    setLastAppliedMetadata(panelMetadata)

    // Switch view mode if specified in metadata
    if (panelMetadata.viewMode) {
      setCommentsPanelViewMode(panelMetadata.viewMode)
    }

    if (panelMetadata.selectedAuthor || panelMetadata.hideResolved !== undefined || panelMetadata.filterText) {
      setThreadPreviewFilterState(prev =>
        produce(prev, draft => {
          if (panelMetadata.selectedAuthor) draft.selectedAuthor = panelMetadata.selectedAuthor
          if (panelMetadata.hideResolved !== undefined) draft.hideResolvedThreads = panelMetadata.hideResolved
          if (panelMetadata.filterText) draft.filterText = panelMetadata.filterText
        }),
      )
      // Apply filterText to issue comments filter state as well
      if (panelMetadata.filterText) {
        const filterText = panelMetadata.filterText
        setIssueCommentsFilterState(prev =>
          produce(prev, draft => {
            draft.filterText = filterText
          }),
        )
      }
    }
  }

  const filteredThreadIds = useFilteredThreadIds(threadPreviews, threadPreviewFilterState)
  const filteredIssueCommentIds = useFilterIssueCommentsIds(mergedConversationComments, issueCommentsFilterState)

  // Extract the selected review ID from filter state
  const selectedReviewId = useMemo(() => {
    if (!threadPreviewFilterState.filterText) return null
    const {reviewId} = parseFilterKeywords(threadPreviewFilterState.filterText)
    return reviewId
  }, [threadPreviewFilterState.filterText])
  const selectedThreadIdInView =
    selectedThreadId && filteredThreadIds.has(selectedThreadId) ? selectedThreadId : undefined
  const isThreadDetailMode = isReviewCommentsViewMode(viewMode) && selectedThreadIdInView !== undefined

  const closeButtonRef = useRef<HTMLButtonElement>(null)
  const footerRef = useRef<CommentsSidePanelFooterHandle>(null)

  // State for comment footer
  const [commentText, setCommentText] = useState('')
  const {mutate: createTimelineComment, isPending: isSubmittingComment} = useCreatePullRequestTimelineCommentMutation()

  const handleCommentTextChange = useCallback((value: string) => {
    setCommentText(value)
  }, [])

  const handleSubmitComment = useCallback(() => {
    if (commentText.trim().length === 0 || isSubmittingComment) return
    createTimelineComment(
      {text: commentText},
      {
        onSuccess: data => {
          setCommentText('')
          requestAnimationFrame(() => {
            ssrSafeDocument
              ?.getElementById(`issue-comment-${data.issueComment.id}`)
              ?.scrollIntoView({behavior: 'smooth'})
          })
        },
      },
    )
  }, [commentText, createTimelineComment, isSubmittingComment])

  const handleQuoteReply = useCallback((quotedText: string) => {
    const wasMinimized = footerRef.current?.expand() ?? false
    if (wasMinimized) {
      setCommentText(prev => {
        if (prev.trim().length > 0) {
          return `${prev}\n\n${quotedText}`
        }
        return quotedText
      })
    } else {
      setCommentText(quotedText)
    }
  }, [])
  const handleBackToThreadList = useCallback(() => {
    clearReplyDetail()
  }, [clearReplyDetail])

  const threadPreviewsAuthorList = useMemo(() => buildThreadPreviewsAuthorList(threadPreviews), [threadPreviews])

  const issueCommentsAuthorList = useMemo(
    () => buildIssueCommentsAuthorList(mergedConversationComments),
    [mergedConversationComments],
  )

  const isCommentPanelFooterEnabled = !isReviewCommentsViewMode(viewMode) && currentUserCanComment
  const commentPanelFooter = isCommentPanelFooterEnabled ? (
    <Dialog.Footer>
      <CommentsSidePanelFooter
        ref={footerRef}
        commentText={commentText}
        onCommentTextChange={handleCommentTextChange}
        onSubmitComment={handleSubmitComment}
        isPending={isSubmittingComment}
      />
    </Dialog.Footer>
  ) : null

  const limitExceeded = pageLimits?.reviewThreadsPaginatedLimitExceeded
  const limitCount = pageLimits?.reviewThreadsPaginatedLimit

  const updatedPullRequestHeader = isFeatureEnabled('updated_pull_request_header')
  const shouldLazyLoadData = isOpen && !isFilesChanged && updatedPullRequestHeader
  const layoutStoreSeeded = useLayoutStoreSeeded()
  const [lazyDataLoaded, setLazyDataLoaded] = useState(false)
  const handleLazyDataLoaded = useCallback(() => setLazyDataLoaded(true), [])
  const isLazyLoading = shouldLazyLoadData && !lazyDataLoaded && !layoutStoreSeeded

  const showRepliesWithReviewFilter = isFeatureEnabled('prs_docked_comments_replies')

  // Subscribe to alive events for live comment updates from other users.
  // This must be above the `if (!isOpen)` guard so the subscription stays
  // active even when the panel is closed. The hook self-activates only
  // once comments have been loaded into the store.
  useIssueCommentLiveUpdates(aliveChannels)

  if (!isOpen) return null

  if (!isCompact) {
    const panelContent = (
      <Panel panelName={DockedPanel.Comments}>
        <Panel.Header>
          {isThreadDetailMode ? (
            <div className="py-2">
              <Button leadingVisual={ArrowLeftIcon} variant="invisible" onClick={handleBackToThreadList}>
                Back to all comments
              </Button>
            </div>
          ) : (
            <CommentPanelHeaderContent
              threadPreviewsAuthorList={threadPreviewsAuthorList}
              issueCommentsAuthorList={issueCommentsAuthorList}
              threadPreviewFilterState={threadPreviewFilterState}
              setThreadPreviewFilterState={setThreadPreviewFilterState}
              issueCommentsFilterState={issueCommentsFilterState}
              setIssueCommentsFilterState={setIssueCommentsFilterState}
              iconOnly
            />
          )}
        </Panel.Header>
        <Panel.Body>
          {shouldLazyLoadData && (
            <ErrorBoundary boundaryName="LazyDataLoader" fallback={null}>
              <Suspense fallback={null}>
                <LazyDataLoader onLoaded={handleLazyDataLoaded} />
              </Suspense>
            </ErrorBoundary>
          )}
          <CommentsPanelBody
            filteredThreadIds={filteredThreadIds}
            filteredIssueCommentIds={filteredIssueCommentIds}
            selectedReviewId={showRepliesWithReviewFilter ? selectedReviewId : null}
            conversationItems={conversationItems}
            handleQuoteReply={handleQuoteReply}
            pathName={pathName}
            pullRequestAuthorLogin={pullRequestAuthorLogin}
            limitExceeded={limitExceeded}
            threadPreviews={threadPreviews}
            totalDiffCommentCount={totalDiffCommentCount}
            limitCount={limitCount}
            isOpen={isOpen}
            isLazyLoading={isLazyLoading}
            onClose={noop}
          />
        </Panel.Body>
        {isCommentPanelFooterEnabled && (
          <Panel.Footer>
            <FeatureFlags flags={{primer_react_css_anchor_positioning: true}}>
              <CommentsSidePanelFooter
                ref={footerRef}
                commentText={commentText}
                onCommentTextChange={handleCommentTextChange}
                onSubmitComment={handleSubmitComment}
                isPending={isSubmittingComment}
              />
            </FeatureFlags>
          </Panel.Footer>
        )}
      </Panel>
    )

    // When the updated_pull_request_header flag is OFF, the panel is rendered
    // as a child of OpenCommentsPanelButton (so it inherits PullRequestStoreProvider
    // scope needed by ThreadPositioningWithStore). It must portal into the
    // central docked-panel target div so it visually appears in the sidebar.
    // When the flag is ON, the panel is rendered directly inside that target div
    // by DockedSidePanelContent and no portal is needed.
    if (!updatedPullRequestHeader) {
      const portalTarget = ssrSafeDocument?.getElementById(DOCKED_SIDE_PANEL_CONTENT_ID)
      if (portalTarget) {
        return createPortal(panelContent, portalTarget)
      }
      return null
    }

    return panelContent
  }

  return (
    <Dialog
      initialFocusRef={closeButtonRef}
      position={{narrow: 'fullscreen', regular: 'right', wide: 'right'}}
      returnFocusRef={returnFocusRef}
      onClose={closePanel}
      renderFooter={() => {
        return commentPanelFooter
      }}
      renderHeader={({dialogLabelId}) => (
        <Dialog.Header>
          <div className="d-flex flex-row flex-justify-between flex-items-center width-full pl-2">
            {isThreadDetailMode ? (
              <>
                <span className="sr-only" id={dialogLabelId}>
                  {getPanelHeading(DockedPanel.Comments)}
                </span>
                <Button leadingVisual={ArrowLeftIcon} variant="invisible" onClick={handleBackToThreadList}>
                  Back to all comments
                </Button>
              </>
            ) : (
              <Dialog.Title id={dialogLabelId}>{getPanelHeading(DockedPanel.Comments)}</Dialog.Title>
            )}
            <IconButton
              ref={closeButtonRef}
              aria-label="Close comments panel"
              icon={XIcon}
              variant="invisible"
              onClick={closePanel}
            />
          </div>
          {!isThreadDetailMode ? (
            <CommentPanelHeaderContent
              threadPreviewsAuthorList={threadPreviewsAuthorList}
              issueCommentsAuthorList={issueCommentsAuthorList}
              threadPreviewFilterState={threadPreviewFilterState}
              setThreadPreviewFilterState={setThreadPreviewFilterState}
              issueCommentsFilterState={issueCommentsFilterState}
              setIssueCommentsFilterState={setIssueCommentsFilterState}
              iconOnly
            />
          ) : null}
        </Dialog.Header>
      )}
    >
      {shouldLazyLoadData && (
        <ErrorBoundary boundaryName="LazyDataLoader" fallback={null}>
          <Suspense fallback={null}>
            <LazyDataLoader onLoaded={handleLazyDataLoaded} />
          </Suspense>
        </ErrorBoundary>
      )}
      <CommentsPanelBody
        limitExceeded={limitExceeded}
        limitCount={limitCount}
        isOpen={isOpen}
        isLazyLoading={isLazyLoading}
        conversationItems={conversationItems}
        onClose={closePanel}
        filteredThreadIds={filteredThreadIds}
        filteredIssueCommentIds={filteredIssueCommentIds}
        handleQuoteReply={handleQuoteReply}
        pathName={pathName}
        selectedReviewId={showRepliesWithReviewFilter ? selectedReviewId : null}
        threadPreviews={threadPreviews}
        totalDiffCommentCount={totalDiffCommentCount}
        pullRequestAuthorLogin={pullRequestAuthorLogin}
      />
    </Dialog>
  )
})
